import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import { collection, getDocs, query, updateDoc, doc } from 'firebase/firestore';

interface Withdrawal {
  id: string;
  transactionId: string;
  amount: number;
  timestamp: any;
  withdrawNumber: string;
  status: string;
}

const WithdrawRequests: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [filteredWithdrawals, setFilteredWithdrawals] = useState<Withdrawal[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWithdrawals = async () => {
      console.log('Fetching withdrawals...');
      try {
        const withdrawalQuery = query(collection(db, 'withdrawals'));
        const withdrawalDocs = await getDocs(withdrawalQuery);

        if (withdrawalDocs.empty) {
          console.warn('No withdrawal requests found.');
          setLoading(false);
          return;
        }

        const withdrawalsData: Withdrawal[] = withdrawalDocs.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const transactionId = String(data.transactionId || 'N/A'); // Ensure correct field name

          console.log('Fetched withdrawal data:', data);

          return {
            id: docSnapshot.id,
            transactionId,
            amount: data.withdrawAmount || 0,
            timestamp: data.timestamp,
            withdrawNumber: data.withdrawNumber || 'Unknown', // Directly fetch withdrawNumber from the withdrawal document
            status: data.status || 'pending',
          };
        });

        setWithdrawals(withdrawalsData);
        setFilteredWithdrawals(withdrawalsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching withdrawals:', error);
        setLoading(false);
      }
    };

    fetchWithdrawals();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term === '') {
      setFilteredWithdrawals(withdrawals);
      return;
    }

    const filtered = withdrawals.filter((withdrawal) => {
      return (
        withdrawal.withdrawNumber.toLowerCase().includes(term) ||
        withdrawal.transactionId.toLowerCase().includes(term)
      );
    });

    setFilteredWithdrawals(filtered);
  };

  const handleStatusChange = async (id: string, newStatus: 'completed' | 'cancelled') => {
    try {
      const withdrawalDoc = doc(db, 'withdrawals', id);
      await updateDoc(withdrawalDoc, { status: newStatus });
      setWithdrawals((prev) =>
        prev.map((withdrawal) =>
          withdrawal.id === id ? { ...withdrawal, status: newStatus } : withdrawal
        )
      );
      setFilteredWithdrawals((prev) =>
        prev.map((withdrawal) =>
          withdrawal.id === id ? { ...withdrawal, status: newStatus } : withdrawal
        )
      );
      console.log(`Transaction ${id} marked as ${newStatus}`);
    } catch (error) {
      console.error(`Error updating withdrawal ${id} status:`, error);
    }
  };

  if (loading) return <p>Loading withdrawals...</p>;

  return (
    <div className="withdraw-requests" style={styles.container}>
      <h1 style={styles.header}>Withdraw Requests</h1>
      <input
        type="text"
        placeholder="Search by withdrawal number or transaction ID"
        value={searchTerm}
        onChange={handleSearch}
        style={styles.searchInput}
      />
      <div className="withdrawals-list" style={styles.cardContainer}>
        {filteredWithdrawals.map((withdrawal) => (
          <div key={withdrawal.id} className="withdrawal-card" style={styles.card}>
            <h3 style={styles.transactionId}>Transaction ID: {withdrawal.transactionId}</h3>
            <p><strong>Withdrawal Number:</strong> {withdrawal.withdrawNumber}</p>
            <p><strong>Amount:</strong> ${withdrawal.amount}</p>
            <p><strong>Status:</strong> {withdrawal.status}</p>
            <p><strong>Timestamp:</strong> {new Date(withdrawal.timestamp?.toDate()).toLocaleString()}</p>
            <div className="actions" style={styles.actions}>
              {withdrawal.status === 'pending' && (
                <button
                  onClick={() => handleStatusChange(withdrawal.id, 'completed')}
                  style={styles.completeButton}
                >
                  Complete
                </button>
              )}
              {withdrawal.status !== 'cancelled' && (
                <button
                  onClick={() => handleStatusChange(withdrawal.id, 'cancelled')}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#1F1F1F',
    color: '#E0E0E0',
    padding: '20px',
    borderRadius: '10px',
  },
  header: {
    color: '#fff',
    fontSize: '24px',
    marginBottom: '20px',
  },
  searchInput: {
    backgroundColor: '#333',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: '5px',
    border: '1px solid #444',
    marginBottom: '20px',
    width: '100%',
    fontSize: '16px',
  },
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px',
  },
  card: {
    backgroundColor: '#2A2A2A',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
    color: '#fff',
    transition: 'transform 0.2s',
  },
  transactionId: {
    color: '#fff',
    fontSize: '20px',
    marginBottom: '10px',
  },
  actions: {
    marginTop: '15px',
  },
  completeButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '12px 20px',
    cursor: 'pointer',
    borderRadius: '5px',
    width: '100%',
    fontSize: '16px',
    transition: 'background-color 0.3s',
  },
  cancelButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    padding: '12px 20px',
    cursor: 'pointer',
    borderRadius: '5px',
    width: '100%',
    fontSize: '16px',
    transition: 'background-color 0.3s',
  },
};

export default WithdrawRequests;
